/**
 * ReactionChip (#989)
 *
 * A single emoji-reaction chip: the emoji glyph + its count, own-reaction
 * highlighted "whisper gold" (#2916, design option C): `accent/40` stroke, no
 * gold fill, count in `oro-gold-light`. Full-strength gold stays reserved for
 * leader/priority surfaces (ADR-UX-006) — the announcement treatment must
 * visibly outrank this chip. (No new glow token per #2784.)
 * Presentational — the container (`MessageReactions`) owns the toggle mutation
 * and the reactor popover; this chip only renders and forwards interaction.
 *
 * Rule 20 reaction-content carve-out: the palette emoji rendered here are
 * user-selected CONTENT (same class as emoji inside a message body), one of the
 * three surfaces the carve-out covers (chips, picker palette rows, reactor
 * lists). All other chrome stays Lucide.
 */

import { forwardRef, type KeyboardEventHandler } from 'react';
import type { MessageReaction } from '../lib/api/messages';

interface ReactionChipProps {
  reaction: MessageReaction;
  /** Toggle the viewer's own reaction for this emoji. */
  onToggle: () => void;
  /**
   * Disabled — no pointer/click/focus at all. Reserve this for the in-flight
   * state (a toggle is pending). Do NOT use it for read-only viewers: a
   * disabled `<button>` receives neither pointer nor keyboard focus, so it can't
   * open the reactor-list peek (George r1). Read-only viewers keep an enabled,
   * non-toggleable chip via `canToggle={false}` instead.
   */
  disabled?: boolean;
  /**
   * Whether the viewer may toggle this reaction. When false the chip stays
   * enabled (so hover/focus peek of the reactor list still works) but presents
   * as non-interactive (default cursor, no hover affordance); the container's
   * `onToggle` early-returns. Defaults to true.
   */
  canToggle?: boolean;
  /** Pointer/focus handlers wire the reactor-list popover from the container. */
  onPointerEnter?: () => void;
  onPointerLeave?: () => void;
  onFocus?: () => void;
  onBlur?: () => void;
  /** Key handler (container closes the reactor popover on Escape — George r3). */
  onKeyDown?: KeyboardEventHandler<HTMLButtonElement>;
  /**
   * Id of the reactor-list popover this chip discloses (George r2). When set,
   * the chip advertises `aria-haspopup="dialog"` and reflects `popoverOpen`
   * via `aria-expanded` / `aria-controls` — the popover is a scrollable dialog,
   * not a `role="tooltip"` (a tooltip must not hold interactive/scrollable
   * content). Focusing the chip opens it.
   */
  popoverId?: string;
  popoverOpen?: boolean;
}

const ReactionChip = forwardRef<HTMLButtonElement, ReactionChipProps>(function ReactionChip(
  { reaction, onToggle, disabled, canToggle = true, onPointerEnter, onPointerLeave, onFocus, onBlur, onKeyDown, popoverId, popoverOpen },
  ref,
) {
  const { emoji, count, mine } = reaction;

  return (
    <button
      ref={ref}
      type="button"
      data-testid={`reaction-chip-${emoji}`}
      aria-pressed={mine}
      aria-label={`${emoji} reaction, ${count}${mine ? ', including you' : ''}`}
      aria-haspopup={popoverId ? 'dialog' : undefined}
      aria-expanded={popoverId ? !!popoverOpen : undefined}
      aria-controls={popoverId && popoverOpen ? popoverId : undefined}
      disabled={disabled}
      onClick={onToggle}
      onPointerEnter={onPointerEnter}
      onPointerLeave={onPointerLeave}
      onFocus={onFocus}
      onBlur={onBlur}
      onKeyDown={onKeyDown}
      className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs leading-none transition-colors disabled:opacity-60 ${
        canToggle ? '' : 'cursor-default'
      } ${
        mine
          ? 'border-accent/40 bg-oro-surface-alt text-text-primary'
          : `border-oro-border bg-oro-surface-alt text-text-secondary${
              canToggle ? ' hover:border-oro-border-hover' : ''
            }`
      }`}
    >
      <span className="text-sm leading-none">{emoji}</span>
      <span className={`tabular-nums${mine ? ' text-oro-gold-light' : ''}`}>{count}</span>
    </button>
  );
});

export default ReactionChip;
